import type {
  Id,
  ResourceType,
  Action,
  SagaCallbacks,
} from '../types';

import * as actionTypes from './actionTypes';

export const requestAddClientEntity = ({
  list,
  payload,
  resourceType,
  callbacks,
}: {
  list: string
  payload: any
  resourceType: ResourceType
  callbacks: SagaCallbacks
}): Action => ({
  type: actionTypes.REQUEST_ADD_CLIENT_ENTITY,
  list,
  payload,
  callbacks,
  resourceType,
});

export const requestInviteClient = ({
  userId,
  payload,
  callbacks,
}: {
  userId: number
  payload: any
  callbacks?: SagaCallbacks
}): Action => ({
  type: actionTypes.REQUEST_INVITE_CLIENT,
  userId,
  payload,
  callbacks,
});

export const requestGroupEntity = (
  groupId: number,
  resourceType: ResourceType,
  requestKey?: string,
): Action => ({
  type: actionTypes.REQUEST_GROUP_ENTITY,
  groupId,
  requestKey,
  resourceType,
});

export const requestGroupEntityAssignments = ({
  status,
  groupId,
  resourceType,
}: {
  status: string
  groupId: number
  resourceType: ResourceType
}): Action => ({
  type: actionTypes.REQUEST_GROUP_ENTITY_ASSIGNMENTS,
  status,
  groupId,
  resourceType,
});

export const requestFetchClient = (
  clientId: number | string,
): Action => ({
  type: actionTypes.REQUEST_FETCH_CLIENT,
  clientId,
});

export const requestFetchClients = ({
  qs,
  list,
  requestKey,
  resourceType,
}: {
  qs?: any
  list?: string
  requestKey?: string
  resourceType?: ResourceType
}): Action => ({
  type: actionTypes.REQUEST_FETCH_CLIENTS,
  qs,
  list,
  requestKey,
  resourceType,
});

export const requestFetchArchivedClients = ({
  qs,
  list,
  requestKey,
  resourceType,
}: {
  qs?: any,
  list?: string,
  requestKey?: string,
  resourceType?: ResourceType
}): Action => ({
  type: actionTypes.REQUEST_FETCH_ARCHIVED_CLIENTS,
  qs,
  list,
  requestKey,
  resourceType,
});

export const requestFetchClientEntity = ({
  list,
  params,
  clientId,
  resourceType,
}: {
  list?: string
  params?: any,
  clientId?: number
  resourceType: ResourceType
}): Action => ({
  type: actionTypes.REQUEST_FETCH_CLIENT_ENTITY,
  list,
  params,
  clientId,
  resourceType,
});

export const requestClientPersonalEntity = ({
  qs,
  clientId,
  entityType,
  requestKey,
  resourceType,
}: {
  qs?: any,
  clientId?: number,
  entityType?: string,
  requestKey?: string,
  resourceType: ResourceType,
}): Action => ({
  type: actionTypes.REQUEST_CLIENT_PERSONAL_ENTITY,
  qs,
  clientId,
  entityType,
  requestKey,
  resourceType,
});

export const requestDeleteClientItems = ({
  entityType,
  resourceType,
  callbacks,
}: {
  entityType?: string,
  resourceType: ResourceType,
  callbacks?: SagaCallbacks,
}): Action => ({
  type: actionTypes.REQUEST_DELETE_CLIENT_ITEMS,
  entityType,
  resourceType,
  callbacks,
});

export const requestDeleteClientFromGroup = ({
  groupId,
  selectedClientIds,
  callbacks,
}: {
  groupId: Id,
  selectedClientIds: Id[],
  callbacks?: SagaCallbacks,
}): Action => ({
  type: actionTypes.REQUEST_DELETE_FROM_GROUP,
  groupId,
  selectedClientIds,
  callbacks,
});

export const requestDeleteClientEntity = ({
  id,
  resourceType,
  callbacks,
}: {
  id: number,
  resourceType: ResourceType,
  callbacks?: SagaCallbacks,
}): Action => ({
  type: actionTypes.REQUEST_DELETE_CLIENT_ENTITY,
  id,
  resourceType,
  callbacks,
});

export const requestDeletePersonalEntity = (
  id: number,
  clientId: number,
  resourceType: ResourceType,
  callbacks: SagaCallbacks,
): Action => ({
  type: actionTypes.REQUEST_DELETE_PERSONAL_ENTITY,
  id,
  clientId,
  resourceType,
  callbacks,
});

export const requestCreatePersonalEntity = ({
  payload,
  clientId,
  resourceType,
  callbacks,
}: {
  payload: {
    name: string,
  },
  clientId: number,
  resourceType: ResourceType,
  callbacks: SagaCallbacks,
}): Action => ({
  type: actionTypes.REQUEST_CREATE_PERSONAL_ENTITY,
  payload,
  clientId,
  resourceType,
  callbacks,
});

export const requestMoveToGroup = (
  callbacks: SagaCallbacks,
  currentEntityId: any,
): Action => ({
  type: actionTypes.REQUEST_MOVE_TO_GROUP,
  callbacks,
  currentEntityId,
});

export const requestAddToGroup = (
  clientId: number,
  groupId: number,
): Action => ({
  type: actionTypes.REQUEST_ADD_TO_GROUP,
  clientId,
  groupId,
});

export const requestUploadGroupAvatar = ({
  file,
  groupId,
  callbacks,
}: {
  file: any
  groupId: number
  callbacks: SagaCallbacks
}) => ({
  type: actionTypes.REQUEST_UPLOAD_GROUP_AVATAR,
  groupId,
  payload: file,
  callbacks,
});

export const requestAssignClient = ({
  payload,
  resourceType,
  listName,
}: {
  payload: any,
  listName: string,
  resourceType: ResourceType,
}): Action => ({
  type: actionTypes.REQUEST_ASSIGN_CLIENT,
  payload,
  resourceType,
  listName,
});

export const requestUnAssignClient = ({
  id,
  resourceType,
}: {
  id: number,
  resourceType: ResourceType,
}): Action => ({
  type: actionTypes.REQUEST_UNASSIGN_CLIENT,
  id,
  resourceType,
});

export const requestAssignGroup = ({
  id,
  payload,
  listName,
}: {
  id: number,
  payload: any,
  listName: string,
}): Action => ({
  type: actionTypes.REQUEST_ASSIGN_GROUP,
  id,
  payload,
  listName,
});

export const requestUnAssignGroup = ({
  id,
  payload,
}: {
  id: number,
  payload: any,
}): Action => ({
  type: actionTypes.REQUEST_UNASSIGN_GROUP,
  id,
  payload,
});

export const requestUploadClientAvatar = ({
  file,
  profileId,
}: {
  file: any;
  profileId: number;
}) => ({
  type: actionTypes.REQUEST_UPLOAD_CLIENT_AVATAR,
  payload: file,
  profileId,
});

export const requestUpdateClientInfo = ({
  id,
  payload,
  callbacks,
}: {
  id: number;
  payload: any;
  callbacks: any;
}) => ({
  type: actionTypes.REQUEST_UPDATE_CLIENT_INFO,
  id,
  payload,
  callbacks,
});

export const requestArchiveClients = (clients: Array<any>) => ({
  type: actionTypes.REQUEST_ARCHIVE_CLIENTS,
  clients,
});

export const requestRestoreClients = (clients: Array<any>) => ({
  type: actionTypes.REQUEST_RESTORE_CLIENTS,
  clients,
});

export const requestBulkArchiveClients = ({
  ids,
  callbacks,
}: {
  ids: Array<number>,
  callbacks?: SagaCallbacks,
}) => ({
  type: actionTypes.REQUEST_BULK_ARCHIVE_CLIENTS,
  ids,
  callbacks,
});

export const requestBulkRestoreClients = ({
  ids,
  callbacks,
}: {
  ids: Array<number>,
  callbacks?: SagaCallbacks,
}) => ({
  type: actionTypes.REQUEST_BULK_RESTORE_CLIENTS,
  ids,
  callbacks,
});

export const requestDashboards = (isPro: boolean) => ({
  type: actionTypes.REQUEST_DASHBOARDS,
  isPro,
});

export const requestMasterPro = () => ({
  type: actionTypes.REQUEST_MASTER_PRO,
});

export const requestNotifications = (userId: number) => ({
  type: actionTypes.REQUEST_NOTIFICATIONS,
  userId,
});

export const requestClientsPerformance = () => ({
  type: actionTypes.REQUEST_CLIENTS_PERFORMANCE,
});

export const requestArchiveTemplate = ({
  id,
  resourceType,
}: {
  id: number,
  resourceType: ResourceType,
}) => ({
  type: actionTypes.REQUEST_ARCHIVE_TEMPLATE,
  id,
  resourceType,
});

export const requestRestoreTemplate = ({
  id,
  resourceType,
}: {
  id: number
  resourceType: ResourceType
}) => ({
  type: actionTypes.REQUEST_RESTORE_TEMPLATE,
  id,
  resourceType,
});

export const requestArchiveAssignment = ({
  id,
  payload,
  callbacks,
}: {
  id: number
  payload: any
  callbacks: SagaCallbacks
}) => ({
  type: actionTypes.REQUEST_ARCHIVE_ASSIGNMENT,
  id,
  payload,
  callbacks,
});

export const requestRestoreAssignment = ({
  id,
  payload,
  callbacks,
}: {
  id: number
  payload: any
  callbacks?: SagaCallbacks
}) => ({
  type: actionTypes.REQUEST_RESTORE_ASSIGNMENT,
  id,
  payload,
  callbacks,
});
